import React, { useEffect, useState, useMemo, useRef } from 'react';
import { formatUserDisplayName } from '@/lib/user-display';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { SectionLayout } from '@/components/section-layout';
import { useOpenEntity } from '@/hooks/useOpenEntity';
import { NavigationService } from '@/services/navigationService';
import { getApiUrl } from '@/lib/api-config';
import { fetchEntity } from '@/lib/entity-api';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { format } from 'date-fns';
import { AlertCircle, FileText, MessageSquare, Paperclip, MoreHorizontal, Trash2, Copy, Download, Upload } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SpecificationForm } from '@/features/project-management/specifications/SpecificationForm';
import { trackSpecificationCreated, trackSpecificationUpdated, trackSpecificationViewed, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
// Types defined locally since @/types/specification doesn't exist


interface Specification {
  id: string;
  specification_number: string | null;
  title: string;
  description: string;
  priority: string;
  project_id: string;
  assignee_id: string;
  created_by: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  assignee?: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
  project?: {
    id: string;
    name: string;
  };
  attachments: { count: number }[];
  comments: { count: number }[];
  attachment_count: number;
}

interface SpecificationComment {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  specification_id: string;
  parent_comment_id: string | null;
  author: {
    id: string;
    display_name?: string | null;
  };
}

interface SpecificationAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  created_at: string;
  created_by: string;
}


const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('default', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};


function SpecificationsPage() {
  const { selectedProject } = useProject();
  const { companyId, user } = useUser();
  const { shouldOpen, entityId } = useOpenEntity('specification');
  // The list: ONE server-driven query (project + search + sort + page) -- search, sort and paging run in the database
  // over every specification the user may see; the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, Specification>({
    entityType: 'specification',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'specification_number', direction: 'asc', sortKey: 'specification_number' },
    mapRow: (row) => ({
      ...row,
      attachments: [{ count: row.attachment_count || 0 }],
      comments: [{ count: row.comment_count || 0 }],
    }),
  });
  const specifications = list.items;
  // Latest values for the effect that opens a record from navigation (read when it runs, never a reason to re-run it).
  const specificationsRef = useRef(specifications);
  specificationsRef.current = specifications;
  const handleRowClickRef = useRef<(row: Specification) => void>(() => {});
  const [selectedSpecification, setSelectedSpecification] = useState<Specification | null>(null);
  const [comments, setComments] = useState<SpecificationComment[]>([]);
  const [attachments, setAttachments] = useState<SpecificationAttachment[]>([]);
  const { projects, users } = useCompanyLookups(!!companyId);
  const [isNewSpecificationOpen, setIsNewSpecificationOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [specificationForm, setSpecificationForm] = useState({
    title: '',
    description: '',
    assignee: '',
    priority: 'medium',
    comments: '',
    projectId: '',
  });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSpecification, setEditingSpecification] = useState<Specification | null>(null);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [originalEditingSpecification, setOriginalEditingSpecification] = useState<Specification | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Check if we should open a specific specification from navigation
  useEffect(() => {
    if (!shouldOpen || !entityId || !list.hasLoaded) return;
    // Clear the state to prevent reopening on refresh
    NavigationService.clearOpenState();
    // The record may not be on the page that is showing: ask the API for it.
    const loaded = specificationsRef.current.find((s) => s.id === entityId);
    (loaded ? Promise.resolve(loaded) : fetchEntity<any>('specification', entityId, { includeCounts: true }))
      .then((row) => handleRowClickRef.current(row))
      .catch((error) => console.error('Error opening specification:', error));
  }, [shouldOpen, entityId, list.hasLoaded]);

  const handleSpecificationClick = (specification: Specification) => {
    setSelectedSpecification(specification);
    // fetchSpecificationDetails will be called via useEffect when editingSpecification changes
  };

  // Map priority to GenericTag priority types (urgent -> high)
  const mapPriorityToTag = (priority: string): 'low' | 'medium' | 'high' => {
    switch (priority.toLowerCase()) {
      case 'low':
        return 'low';
      case 'medium':
        return 'medium';
      case 'high':
      case 'urgent':
        return 'high'; // Map urgent to high
      default:
        return 'medium';
    }
  };

  // Map specification statuses to GenericTag status types
  const mapSpecificationStatusToTag = (status: string): 'draft' | 'sent' | 'approved' | 'rejected' | 'pending' | 'in_progress' | 'completed' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft';
      case 'sent':
        return 'sent';
      case 'pending':
        return 'pending';
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'rejected';
      default:
        return 'draft';
    }
  };


  const handleCreateSpecification = async (formData: any) => {
    try {
      console.log('Starting specification creation...');
      if (!companyId || !user?.id) {
        setError('No company ID or user ID found');
        return;
      }
      console.log('User found:', user.id);

      // Validate required fields
      if (!formData.title || !formData.project_id) {
        const missing = [];
        if (!formData.title) missing.push('Title');
        if (!formData.project_id) missing.push('Project');
        const errorMsg = `Please fill in all required fields: ${missing.join(', ')}`;
        console.log('Validation failed:', errorMsg);
        setError(errorMsg);
        return;
      }

      console.log('Creating specification with data:', {
        ...formData,
        company_id: companyId,
        created_by: user.id
      });

      // Use generic API for specification creation
      const response = await fetch(getApiUrl('api/entities/specification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          project_id: formData.project_id,
          assignee_id: formData.assignee_id || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create specification');
      }

      const result = await response.json();
      const data = result.data;

      console.log('Specification created successfully:', data);

      // Track successful specification creation with PostHog
      trackSpecificationCreated({
        specification_id: data.id,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        project_id: formData.project_id,
        assignee_id: formData.assignee_id,
        attachment_count: formData.attachmentsFiles?.length || 0,
        company_id: companyId,
        created_by: user.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'specification_creation',
        success: true
      });

      // If files were queued in the form, upload them now that we have an ID
      if (data?.id && Array.isArray(formData.attachmentsFiles) && formData.attachmentsFiles.length > 0) {
        try {
          const { uploadSpecificationAttachments } = await import('@/features/project-management/specifications/store');
          const uploaded = await uploadSpecificationAttachments(data.id, formData.attachmentsFiles, companyId, user.id);
          console.log('Queued attachments uploaded after create', { count: uploaded.length });
        } catch (uploadAfterCreateErr) {
          console.error('Error uploading queued attachments after create:', uploadAfterCreateErr);
        }
      }
      list.reload();
      setIsNewSpecificationOpen(false);
      setError(null);
    } catch (error: any) {
      console.error('Error creating specification:', error);
      
      // Track failed form submission with PostHog
      trackFormSubmission({
        form_type: 'specification_creation',
        success: false,
        error_message: error.message || 'Failed to create specification'
      });
      
      setError(error.message || 'Failed to create specification');
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCreateSpecification(specificationForm);
  };

  const handleRowSelect = (specificationId: string) => {
    setSelectedRows(prev => {
      if (prev.includes(specificationId)) {
        return prev.filter(id => id !== specificationId);
      }
      return [...prev, specificationId];
    });
  };

  // Handle copy selected specifications
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one specification to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy specifications');
      return;
    }

    try {
      const selectedSpecs = specifications.filter(s => selectedRows.includes(s.id));
      let successCount = 0;
      let errorCount = 0;

      // Copy each selected specification
      for (const spec of selectedSpecs) {
        try {
          const response = await fetch(getApiUrl('api/entities/specification'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              project_id: spec.project_id,
              title: spec.title,
              description: spec.description || null,
              status: 'pending', // Always set to pending for copies
              priority: spec.priority || 'medium',
              assignee_id: spec.assignee_id || null,
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error copying specification:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error copying specification:', error);
          errorCount++;
        }
      }

      // Refresh the list
      list.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Copied ${successCount} specification(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully copied ${successCount} specification(s).`);
      }
    } catch (error) {
      console.error('Error copying specifications:', error);
      alert('Error copying specifications. Please try again.');
    }
  };

  // Handle delete selected specifications
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one specification to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} specification(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete specifications');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const specificationId of selectedRows) {
        try {
          const response = await fetch(getApiUrl(`api/entities/specification/${specificationId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error deleting specification:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error deleting specification:', error);
          errorCount++;
        }
      }

      // Refresh the list
      list.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Deleted ${successCount} specification(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully deleted ${successCount} specification(s).`);
      }
    } catch (error) {
      console.error('Error deleting specifications:', error);
      alert('Error deleting specifications. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY specification of the
  // current project + search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'specifications', 'specifications');

  const handleRowClick = (specification: Specification) => {
    // The form loads its own comments and attachments when it opens.
    setEditingSpecification(specification);
    setOriginalEditingSpecification(specification);
    setIsEditModalOpen(true);
  };

  handleRowClickRef.current = handleRowClick;

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  const getAssigneeName = (assigneeId: string | null) => {
    if (!assigneeId) return 'Unassigned';
    const user = users.find(u => u.id === assigneeId);
    return user?.full_name || assigneeId;
  };

  const handleSaveChanges = async (formData: any) => {
    try {
      if (!editingSpecification) return;

      // Use generic API for specification update
      const response = await fetch(getApiUrl(`api/entities/specification/${editingSpecification.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          project_id: formData.project_id,
          assignee_id: formData.assignee_id || null,
          priority: formData.priority
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update specification');
      }

      const result = await response.json();
      const data = result.data;

      // Track successful specification update with PostHog
      trackSpecificationUpdated({
        specification_id: editingSpecification.id,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        project_id: formData.project_id,
        assignee_id: formData.assignee_id,
        company_id: companyId,
        updated_by: user?.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'specification_update',
        success: true
      });

      // Update the specifications list with the edited specification
      list.setItems((prev) => prev.map((s) => (s.id === editingSpecification.id ? { ...s, ...data } : s)));
      
      setIsEditModalOpen(false);
      setEditingSpecification(null);
      setOriginalEditingSpecification(null);
    } catch (error: any) {
      console.error('Error updating specification:', error);
      setError(error.message || 'Failed to update specification');
    }
  };

  const handleCloseEditModal = () => {
    if (JSON.stringify(editingSpecification) !== JSON.stringify(originalEditingSpecification)) {
      setIsDiscardModalOpen(true);
    } else {
      setIsEditModalOpen(false);
      setEditingSpecification(null);
      setOriginalEditingSpecification(null);
    }
  };

  const handleDiscardChanges = () => {
    setIsDiscardModalOpen(false);
    setIsEditModalOpen(false);
    setEditingSpecification(null);
    setOriginalEditingSpecification(null);
  };

  // Add this new function to group comments
  const groupComments = (comments: SpecificationComment[]) => {
    const grouped: SpecificationComment[][] = [];
    let currentGroup: SpecificationComment[] = [];

    comments.forEach((comment, index) => {
      if (index === 0) {
        currentGroup.push(comment);
        return;
      }

      const prevComment = comments[index - 1];
      const timeDiff = Math.abs(
        new Date(comment.created_at).getTime() - new Date(prevComment.created_at).getTime()
      );
      const sameAuthor = comment.created_by === prevComment.created_by;

      if (sameAuthor && timeDiff <= 60000) { // 60000ms = 1 minute
        currentGroup.push(comment);
      } else {
        if (currentGroup.length > 0) {
          grouped.push([...currentGroup]);
        }
        currentGroup = [comment];
      }
    });

    if (currentGroup.length > 0) {
      grouped.push(currentGroup);
    }

    return grouped;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  // Define table columns (placed after all handler functions to avoid hoisting issues)
  const columns: GenericTableColumn<Specification>[] = [
    {
      key: 'specification_number',
      header: 'Specification ID',
      width: '8rem',
      sortable: true,
      sortKey: 'specification_number',
      render: (row) => <span className="font-medium">{row.specification_number || '-'}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      width: '12.5rem', // 200px = 12.5rem
      sortable: true,
      sortKey: 'title',
      render: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'project',
      render: (row) => getProjectName(row.project_id),
    },
    {
      key: 'assignee_id',
      header: 'Assignee',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'assignee',
      render: (row) => getAssigneeName(row.assignee_id),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '6.5rem',
      sortable: true,
      sortKey: 'priority',
      render: (row) => (
        <GenericTag 
          type="priority" 
          value={mapPriorityToTag(row.priority)}
        />
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '6.5rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'attachments',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.attachments?.[0]?.count || 0,
    },
    {
      key: 'comments',
      header: 'Comments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.comments?.[0]?.count || 0,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  if (!list.hasLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SectionLayout title="Specifications">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                trackButtonClick('new_specification_button', { context: 'specifications' });
                setIsNewSpecificationOpen(true);
              }}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleRowClick}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
        />

        <SpecificationForm
          mode="create"
          projects={projects}
          users={users}
          onSubmit={handleCreateSpecification}
          onCancel={() => setIsNewSpecificationOpen(false)}
          onChanged={list.reload}
          isOpen={isNewSpecificationOpen}
          error={error}
        />

        <Dialog open={isDiscardModalOpen} onOpenChange={setIsDiscardModalOpen}>
          <DialogContent className="sm:max-w-[25rem]">
            <DialogHeader>
              <DialogTitle>Discard Changes?</DialogTitle>
            </DialogHeader>
            <div className="flex justify-end gap-4 mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDiscardModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDiscardChanges}
              >
                Discard Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {editingSpecification && (
          <SpecificationForm
            mode="edit"
            specification={editingSpecification}
            projects={projects}
            users={users}
            onSubmit={handleSaveChanges}
            onCancel={handleCloseEditModal}
            onChanged={list.reload}
            isOpen={isEditModalOpen}
            error={error}
          />
        )}

        <Dialog open={!!selectedSpecification} onOpenChange={() => setSelectedSpecification(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedSpecification?.title}</DialogTitle>
            </DialogHeader>
            {selectedSpecification && (
              <div className="space-y-6">
                {/* Specification Details */}
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="text-secondary">Description</div>
                      <div className="table-text">{selectedSpecification.description || 'No description'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <GenericTag 
                        type="priority" 
                        value={mapPriorityToTag(selectedSpecification.priority)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-secondary">Assignee</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="h-4 w-4 bg-gray-300 rounded-full" />
                        <span className="table-text">{selectedSpecification.assignee?.full_name || 'Unassigned'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachments Section */}
                <div>
                  <h3 className="subheading-text mb-2">Attachments</h3>
                  <div className="space-y-2">
                    {attachments.length > 0 ? (
                      attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between p-2 rounded-md border"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <span className="table-text">{attachment.file_name}</span>
                          </div>
                          <Button variant="ghost" size="sm">
                            Download
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-secondary">No attachments</div>
                    )}
                  </div>
                </div>

                {/* Comments Section */}
                <div>
                  <h3 className="subheading-text mb-2">Comments</h3>
                  <div className="space-y-4">
                    {comments.length > 0 ? (
                      comments.map((comment) => (
                        <div key={comment.id} className="flex gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gray-200" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="form-text font-medium">
                                {formatUserDisplayName(comment.author)}
                              </span>
                              <span className="text-muted">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <div className="mt-1 comment-text">{comment.content}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-secondary">No comments yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </SectionLayout>
  );
} 

export default SpecificationsPage;
